export type Role = 'admin' | 'member' | 'auditor';
export type Status = 'open' | 'treating' | 'accepted' | 'closed';

export interface Membership { tenantId: string; slug: string; name: string; industry: string; role: Role }
export interface Me { user: { id: string; email: string; displayName: string }; memberships: Membership[] }
export interface Member { id: string; displayName: string; email: string; role: Role }
export interface Risk {
  id: string; tenantId: string; title: string; description: string; likelihood: number; impact: number; score: number;
  status: Status; treatment: string; ownerUserId: string | null; updatedAt: string; version: number;
  residualLikelihood: number | null; residualImpact: number | null; residualScore: number | null;
}
export interface AuditEvent { id: string; action: string; entityId: string; actorUserId: string | null; occurredAt: string }
export interface PublicConfig {
  authMode: 'dev' | 'cognito';
  cognito: { issuer: string; clientId: string; domain?: string } | null;
  release: string;
}

export class ApiError extends Error {
  constructor(public status: number, public body: { error?: string; issues?: { path: string[]; message: string }[] }) {
    super(body.error ?? `HTTP ${status}`);
  }
}

export function createClient(getToken: () => string | null) {
  async function call<T>(method: string, url: string, body?: unknown): Promise<T> {
    const token = getToken();
    const res = await fetch(url, {
      method,
      headers: {
        ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
        ...(token ? { authorization: `Bearer ${token}` } : {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    if (res.status === 204) return undefined as T;
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new ApiError(res.status, json);
    return json as T;
  }
  const t = (id: string) => `/api/tenants/${id}`;
  return {
    me: () => call<Me>('GET', '/api/me'),
    members: (tenant: string) => call<{ members: Member[] }>('GET', `${t(tenant)}/members`),
    risks: (tenant: string) => call<{ risks: Risk[] }>('GET', `${t(tenant)}/risks`),
    createRisk: (tenant: string, body: Partial<Risk>) => call<{ risk: Risk }>('POST', `${t(tenant)}/risks`, body),
    updateRisk: (tenant: string, id: string, body: Partial<Risk>) =>
      call<{ risk: Risk }>('PATCH', `${t(tenant)}/risks/${id}`, body),
    deleteRisk: (tenant: string, id: string) => call<void>('DELETE', `${t(tenant)}/risks/${id}`),
    audit: (tenant: string) => call<{ events: AuditEvent[] }>('GET', `${t(tenant)}/audit-events`),
  };
}
export type Client = ReturnType<typeof createClient>;

export async function fetchConfig(): Promise<PublicConfig> {
  return (await fetch('/api/config')).json();
}

export async function devLogin(email: string): Promise<string> {
  const res = await fetch('/dev/token', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) throw new Error('dev login failed');
  return (await res.json()).accessToken;
}
